<template>
  <div class="app-main">
    <!--
      Three regions - opponents, the card in play, and you - placed by CSS
      grid rather than nested, so the same markup can stack on a phone and
      sit side by side on a tablet. See main.scss's .app-main.
    -->
    <!-- Async room: who the game is waiting on (src/store/asyncPlay.js). -->
    <p v-if="waitingBanner" class="waiting-banner" :class="{ 'is-mine': waitingOnMe }">{{ waitingBanner }}</p>
    <section class="opponents">
      <div
        v-for="player in opponents"
        :key="player.id"
        class="opponent"
        :class="{ 'opponent--active': player.id === activePlayer?.id }"
      >
        <span class="opponent__name">
          {{ player.name }}
          <span v-if="isAbsent(player)" class="seat-gone" title="Disconnected - a bot is playing this hand">
            away
          </span>
          <!--
            Always rendered, only faded in and out. Mounting it on each bot's
            turn widened the name cell, which is `auto`-sized, and shoved the
            counters and cards sideways every time a seat started thinking
            (Matt, 2026-09-30: "the card kinda pops in and out of their little
            area and it just looks bad").
          -->
          <span
            class="thinking-dots"
            :class="{ 'thinking-dots--on': player.id === thinkingPlayerId }"
            aria-hidden="true"
          >
            <span>.</span><span>.</span><span>.</span>
          </span>
        </span>
        <span class="opponent__supply">
          <CounterSupply :count="player.counters" />
          <!--
            What this seat just spent or collected, called out as it happens.
            Bug report (Matt, 2026-08-28): the coarse band alone was too vague -
            "I'd rather have more clear alerts what they spent when they
            actually buy something, so then it's a count as in the real game.
            Eventually, you can see what somebody has."

            So the TOTALS stay hidden (rulebook p2 HINT 4, and Matt's own call
            on 2026-08-26) and the MOVEMENT is made plain instead: every counter
            paid and every pile collected is announced on the seat that did it.
            That is exactly the information a person at a real table gets by
            watching hands, and it makes the count derivable by anyone who cares
            to follow it - without the app doing the bookkeeping for them.

            Keyed on the log length so a run of identical declines re-triggers
            the animation each time instead of sitting there statically.

            It sits INSIDE the supply cell and is absolutely positioned over it.
            As a fourth child of a three-column grid it wrapped onto a second
            row, pushing the hand down under the name and back up again on
            every single bot move - the jumping in the 2026-09-30 report.
          -->
          <transition name="ledger">
            <span
              v-if="lastMoveFor(player)"
              :key="game.log.length"
              class="ledger-flash"
              :class="`ledger-flash--${lastMoveFor(player).kind}`"
            >{{ lastMoveFor(player).text }}</span>
          </transition>
        </span>
        <div class="opponent__cards">
          <CardRuns :cards="player.cards" />
        </div>
      </div>

      <!--
        Your own seat, closing the circle. Bug report (Matt, 2026-08-28):
        "It's not really clear to me where I fall in the order at the top of
        the page. If I'm at the bottom of the order at the top or somewhere in
        the middle. I must be at the bottom. I don't know, it's unclear."

        The list only ever held OPPONENTS, in the order play reaches them
        after you - so your own place in the cycle was implied and nothing
        said it. This marks it: the strip now reads as the whole table, and
        it's obvious that play comes back round to you at the end.
      -->
      <div
        class="opponent opponent--self"
        :class="{ 'opponent--active': human && activePlayer?.id === human.id }"
      >
        <span class="opponent__name">{{ human?.name || 'You' }}</span>
        <span class="opponent__self-tag">your seat</span>
      </div>
    </section>

    <section class="table-centre">
      <p class="deck-note">
        <template v-if="match && match.rounds > 1">
          Round {{ match.round }} of {{ match.rounds }}
          <span aria-hidden="true">·</span>
        </template>
        {{ cardsLeft }} {{ cardsLeft === 1 ? 'card' : 'cards' }} left
        <span aria-hidden="true">·</span>
        9 never dealt
      </p>

      <div class="card-in-play">
        <CardFace :value="game.currentCard" size="table" />
        <div class="pile-on-card">
          <span class="pile-total">{{ game.countersOnCard }}</span>
          <span class="pile-label">on card</span>
          <div v-if="game.countersOnCard > 0" class="pile-dots">
            <span
              v-for="n in Math.min(game.countersOnCard, 12)"
              :key="n"
              class="counter-dot"
            />
          </div>
        </div>
      </div>
    </section>

    <section class="you">
      <div class="you__top">
        <span class="you__label">
          {{ human ? human.name : 'You' }}
          <template v-if="turnNotice"> · {{ turnNotice }}</template>
        </span>
        <!--
          Kept short deliberately. The long version ("score if it ended now")
          overflowed .you__top on a narrow viewport and clipped the number
          itself off the right edge - the one part that had to be readable.
        -->
        <!--
          `human` is null when no seat carries my uid - a spectator, or (the
          Sentry crash, 2026-10-09) a solo game started while still bound to
          an online room. Show "You" with no hand rather than crash the table.
        -->
        <span v-if="human" class="you__score" title="Your score if the game ended right now">
          <CounterSupply :count="human.counters" exact />
          <span aria-hidden="true"> · </span>
          score <strong>{{ currentScore }}</strong>
        </span>
      </div>

      <CardRuns v-if="human" :cards="human.cards" />

      <!--
        Take on the left, No Thanks! on the right (Matt, 2026-09-18: "let's
        switch the no thanks and take cards so the no thanks card is on the
        right"). DOM order, not a CSS `order`, so the reading order, the tab
        order and what you see are the same thing.
      -->
      <div class="actions" :class="{ 'actions--nudge': nudging }">
        <button
          class="action"
          :class="mustTake ? 'action--forced' : 'action--take'"
          :disabled="!myTurn"
          @click="take"
        >
          {{ mustTake ? 'Must take' : 'Take card' }}
          <small>{{ takeSubtitle }}</small>
        </button>
        <button
          class="action action--decline"
          :disabled="!myTurn || !canDecline"
          @click="decline"
        >
          No Thanks!
          <small>pay 1 counter</small>
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { waitingLine, waitingOnMe as isWaitingOnMe } from '../../store/asyncPlay.js';
import { cardPoints } from '@/engine/scoring';
import { netCostOfTaking } from '@/engine/ai/heuristicBot';
import CardFace from './CardFace.vue';
import CardRuns from './CardRuns.vue';
import CounterSupply from './CounterSupply.vue';

// Report 2026-10-09 (Łukasz): "give it reminder after 5 seconds that it is
// now your turn". If your turn has sat untouched this long, the two buttons
// start to glow - a box-shadow only, so nothing in the row resizes (see the
// jumpiness note in CLAUDE.md). Taking keeps the turn (p2), so every move
// restarts the clock rather than just a change of seat.
export const NUDGE_AFTER_MS = 5000;
const TURN_TITLE = 'Your turn · ';

export default {
  name: 'GameTable',
  components: { CardFace, CardRuns, CounterSupply },
  data() {
    return { nudging: false };
  },
  computed: {
    ...mapState(['game', 'thinkingPlayerId', 'presence', 'roomId']),
    ...mapGetters([
      'activePlayer', 'canDecline', 'cardsLeft', 'isHumanTurn', 'match',
      'isOnline', 'isMyOnlineTurn', 'myUid',
    ]),
    /**
     * Online, "me" is the seat carrying my own uid; offline it's the one
     * non-bot seat. Everything below keys off this rather than assuming seat
     * zero, because online the human seats are all real people.
     */
    human() {
      if (this.isOnline) {
        return this.game.players.find((p) => p.id === this.myUid) || null;
      }
      return this.$store.getters.humanPlayer;
    },
    opponents() {
      if (!this.isOnline) return this.$store.getters.opponentsInTurnOrder;
      const me = this.human;
      const { players } = this.game;
      const start = me ? me.seat : 0;
      return players
        .map((_, i) => players[(start + 1 + i) % players.length])
        .filter((p) => p.id !== me?.id);
    },
    waitingOnMe() { return isWaitingOnMe(this.$store.state.roomMeta, this.myUid); },
    waitingBanner() {
      const meta = this.$store.state.roomMeta;
      if (!meta?.async || !this.game) return '';
      const nameOf = (id) => this.game.players.find((p) => p.id === id)?.name || 'someone';
      return waitingLine(meta, this.myUid, nameOf);
    },
    myTurn() {
      return this.isOnline ? this.isMyOnlineTurn : this.isHumanTurn;
    },
    mustTake() {
      return this.myTurn && !this.canDecline;
    },
    turnNotice() {
      if (this.myTurn) return 'your turn';
      // Solo, a bot's turn is a blur of thinking dots; only name it online.
      if (!this.isOnline) return '';
      const active = this.activePlayer;
      if (!active) return '';
      return `${active.name}'s turn`;
    },
    /** Changes on every move, so the nudge clock restarts after a take. */
    turnKey() {
      return this.myTurn ? `mine:${this.game.log.length}` : '';
    },
    currentScore() {
      if (!this.human) return 0;
      return cardPoints(this.human.cards) - this.human.counters;
    },
    /**
     * What taking this card would actually do to your score - the same
     * marginal-cost calculation the bot uses, shown rather than hidden.
     *
     * This is deliberate: the one genuinely hard thing about No Thanks! is
     * spotting that a card bridging two runs can be worth LESS than nothing,
     * and that is a property of the printed cards in front of you, not
     * secret information. A physical player can see it by looking at their
     * own tableau. Surfacing it removes arithmetic, not judgement - the hard
     * call, whether to wait and let the pile grow, is untouched.
     */
    takeSubtitle() {
      if (!this.human) return '';
      const delta = netCostOfTaking(this.human.cards, this.game.currentCard, this.game.countersOnCard);
      if (delta === 0) return 'free';
      return delta < 0 ? `${delta} to your score` : `+${delta} to your score`;
    },
  },
  watch: {
    turnKey: {
      immediate: true,
      handler(key) {
        this.clearNudge();
        if (key) this.nudgeTimer = setTimeout(this.startNudge, NUDGE_AFTER_MS);
      },
    },
  },
  mounted() {
    document.addEventListener('visibilitychange', this.onVisibility);
  },
  beforeUnmount() {
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.clearNudge();
  },
  methods: {
    startNudge() {
      this.nudgeTimer = null;
      this.nudging = true;
      // A background tab can't see the glow; its title can.
      if (document.hidden && !document.title.startsWith(TURN_TITLE)) {
        document.title = TURN_TITLE + document.title;
      }
    },
    clearNudge() {
      clearTimeout(this.nudgeTimer);
      this.nudgeTimer = null;
      this.nudging = false;
      this.restoreTitle();
    },
    restoreTitle() {
      if (document.title.startsWith(TURN_TITLE)) {
        document.title = document.title.slice(TURN_TITLE.length);
      }
    },
    onVisibility() {
      if (document.hidden) {
        if (this.nudging && !document.title.startsWith(TURN_TITLE)) document.title = TURN_TITLE + document.title;
      } else {
        this.restoreTitle();
      }
    },
    /**
     * The most recent counter movement, if it belongs to this seat.
     *
     * Reads only the last log entry on purpose: this is a callout for the
     * thing that just happened, not a running ledger. A decline spends
     * exactly one counter; a take collects the pile that was on the card,
     * which can be zero - and "took the card" is still worth saying then,
     * because it's the moment a card leaves the table.
     */
    lastMoveFor(player) {
      const last = this.game.log[this.game.log.length - 1];
      if (!last || last.playerId !== player.id) return null;
      if (last.kind === 'decline') return { kind: 'spend', text: '−1' };
      if (last.kind === 'take') {
        return {
          kind: 'gain',
          text: last.counters > 0 ? `+${last.counters}` : 'took it',
        };
      }
      return null;
    },
    take() { this.$store.dispatch('take'); },
    decline() { this.$store.dispatch('decline'); },
    isAbsent(player) {
      return this.isOnline && !player.isBot && !this.presence[player.id];
    },
  },
};
</script>
